import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search,
  ExpandMore,
  CheckCircle,
  Cancel,
  Warning
} from '@mui/icons-material';
import axios from 'axios';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const ProductPage = () => {
  // Устанавливаем заголовок страницы
  useDocumentTitle('Товар по ссылке');
  
  const [productUrl, setProductUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const [error, setError] = useState('');

  const countryMap = {
    us: { name: "США", flag: "🇺🇸", currency: "$" },
    pl: { name: "Польша", flag: "🇵🇱", currency: "zł" },
    uk: { name: "Великобритания", flag: "🇬🇧", currency: "£" },
    sp: { name: "Испания", flag: "🇪🇸", currency: "€" },
    it: { name: "Италия", flag: "🇮🇹", currency: "€" },
    tr: { name: "Турция", flag: "🇹🇷", currency: "₺" },
    cn: { name: "Китай", flag: "🇨🇳", currency: "¥" },
    vn: { name: "Вьетнам", flag: "🇻🇳", currency: "₫" },
    kz: { name: "Казахстан", flag: "🇰🇿", currency: "₸" },
    by: { name: "Беларусь", flag: "🇧🇾", currency: "Br" }
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'in_stock':
        return 'success';
      case 'low_on_stock':
        return 'warning';
      case 'out_of_stock':
        return 'error';
      default:
        return 'default';
    }
  };


  const getAvailabilityIcon = (availability) => {
    switch (availability) {
      case 'in_stock':
        return <CheckCircle />;
      case 'low_on_stock':
        return <Warning />;
      case 'out_of_stock':
        return <Cancel />;
      default:
        return <Cancel />;
    }
  };

  const handleSearch = async () => {
    if (!productUrl.trim()) {
      setError('Пожалуйста, введите ссылку на товар');
      return;
    }

    setLoading(true);
    setError('');
    setProductData(null);

    try {
              const response = await axios.get(`https://api.dudu-shopping.ru:3001/api/product?url=${encodeURIComponent(productUrl)}`);
      setProductData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при получении данных о товаре');
    } finally {
      setLoading(false);
    }
  };



  return (
    <Box>
      <Typography variant="h3" component="h1" sx={{ mb: 4, textAlign: 'center', fontWeight: 300 }}>
        Поиск товара Zara
      </Typography>

      {/* Search Form */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 400 }}>
          Введите ссылку на товар
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            label="Ссылка на товар Zara"
            placeholder="https://www.zara.com/tr/en/products/..."
            value={productUrl}
            onChange={(e) => setProductUrl(e.target.value)}
            variant="outlined"
            sx={{ flexGrow: 1, minWidth: 300 }}
          />
          <Button
            variant="contained"
            size="large"
            onClick={handleSearch}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            sx={{ px: 4, py: 1.5, textTransform: 'none' }}
          >
            {loading ? 'Поиск...' : 'Найти'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Results */}
      {productData && (
        <Box>
          <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 400 }}>
            Результаты поиска
          </Typography>

          {Object.entries(productData).map(([country, data]) => {
            if (data.error) return null;
            
            return data.map((product, productIndex) => (
              <Card key={`${country}-${productIndex}`} sx={{ mb: 3, borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {countryMap[country]?.flag || '🏳️'}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 500 }}>
                        {countryMap[country]?.name || country.toUpperCase()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Артикул: {product.displayReference}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 400 }}>
                    {product.name}
                  </Typography>

                  {product.colors && product.colors.length > 0 && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          Цвета и размеры ({product.colors.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={3}>
                          {product.colors.map((color, colorIndex) => (
                            <Grid item xs={12} md={6} key={colorIndex}>
                              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      backgroundColor: color.hexCode,
                                      border: '2px solid #ddd',
                                      mr: 2
                                    }}
                                  />
                                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    {color.name}
                                  </Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  Цена: {color.price !== 'Нет данных' ? `${color.price}` : 'Цена не указана'}
                                </Typography>

                                {color.images && color.images.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                      Изображения:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {color.images.slice(0, 3).map((image, imgIndex) => (
                                        <img
                                          key={imgIndex}
                                          src={image}
                                          alt={`${color.name} ${imgIndex + 1}`}
                                          style={{
                                            width: 80,
                                            height: 80,
                                            objectFit: 'cover',
                                            borderRadius: 8
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {color.sizes && color.sizes.length > 0 && (
                                  <Box>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                      Размеры:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {color.sizes.map((size, sizeIndex) => (
                                        <Chip
                                          key={sizeIndex}
                                          label={size.name}
                                          color={getAvailabilityColor(size.availability)}
                                          icon={getAvailabilityIcon(size.availability)}
                                          size="small"
                                          variant="outlined"
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </CardContent>
              </Card>
            ));
          })}
        </Box>
      )}

      {/* Example URLs */}
      {!productData && !loading && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 3, backgroundColor: '#f8f9fa' }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 500 }}>
            Примеры ссылок для тестирования:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Турция:"
                secondary="https://www.zara.com/tr/en/products/..."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Испания:"
                secondary="https://www.zara.com/sp/en/products/..."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Польша:"
                secondary="https://www.zara.com/pl/en/products/..."
              />
            </ListItem>
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default ProductPage;
